// src/pages/TiKaneSenpPage.jsx
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Table,
  Button,
  InputGroup,
  Form,
  Pagination,
  Row,
  Col,
  Badge,
  Alert,
} from 'react-bootstrap';
import ProtectedSection from '../components/ProtectedSection';
import { AuthContext } from '../context/AuthContext';
import tikaneSenpService from '../services/tikaneSenpService';
import TiKaneSenpModal from '../components/TiKaneSenpModal';
import TiKaneSenpMinorModal from '../components/TiKaneSenpMinorModal';
import TransactionTiKaneSenpModal from '../components/TransactionTiKaneSenpModal';
import TiKaneSenpViewModal from '../components/TiKaneSenpViewModal';

// Helpers recherche (accent/casse-insensibles)
const normalize = (str) => {
  if (str == null) return '';
  return String(str)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

export default function TiKaneSenpPage() {
  const { hasRight } = useContext(AuthContext);

  const canView = hasRight('TIKANE_SENP_VIEW');
  const canCreate = hasRight('TIKANE_SENP_CREATE');
  const canUpdate = hasRight('TIKANE_SENP_UPDATE');
  const canDelete = hasRight('TIKANE_SENP_DELETE');
  const canDeposit = hasRight('TRANS_DEPOSIT');
  const canWithdraw = hasRight('TRANS_WITHDRAW');
  const canViewTrans = hasRight('TRANS_VIEW');

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [filter, setFilter] = useState('');
  const [sortMode, setSortMode] = useState('recent');

  // pagination UI (client-side) avec taille de page dynamique
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Modals création / édition
  const [showAdultModal, setShowAdultModal] = useState(false);
  const [showMinorModal, setShowMinorModal] = useState(false);
  const [adultInitial, setAdultInitial] = useState(null);
  const [minorInitial, setMinorInitial] = useState(null);

  // Modal opérations
  const [txModalShow, setTxModalShow] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState(null);

  // Modal lecture seule (Voir)
  const [viewModalShow, setViewModalShow] = useState(false);
  const [viewTiKaneSenpId, setViewTiKaneSenpId] = useState(null);

  const fetchAccounts = () => {
    if (!canView) return;
    setLoading(true);
    setErrorMsg('');

    // On charge large et on filtre/pagine côté front (comme Ti Kanè)
    tikaneSenpService
      .list({ page: 1, pageSize: 10000 })
      .then((data) => {
        setAccounts(data.data || []);
      })
      .catch((e) => {
        console.error(e);
        setErrorMsg('Erreur lors du chargement des comptes Ti Kanè Senp.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchAccounts, [canView]);

  // Filtrage + tri (affichage)
  const filteredAndSorted = useMemo(() => {
    const q = normalize(filter);
    let list = [...accounts];

    if (q) {
      list = list.filter((acc) => {
        const isClient = !!acc.clientId && acc.client;
        const isMinor = !!acc.minorId && acc.minor;

        const clientName = isClient
          ? `${acc.client?.firstName || ''} ${acc.client?.lastName || ''}`
          : '';
        const minorName = isMinor
          ? `${acc.minor?.firstName || ''} ${acc.minor?.lastName || ''}`
          : '';

        const typeLabel = isClient ? 'adulte client adulte' : isMinor ? 'mineur' : '';
        const ownerLabel = isClient
          ? clientName || `Client #${acc.clientId}`
          : isMinor
          ? minorName || `Mineur #${acc.minorId}`
          : '';

        const balanceStr =
          typeof acc.balance !== 'undefined' && acc.balance !== null
            ? String(acc.balance)
            : '';

        const hay = normalize(
          [
            acc.id || '',
            acc.accountNumber || '',
            typeLabel,
            ownerLabel,
            clientName,
            minorName,
            acc.status || '',
            balanceStr,
          ].join(' ')
        );

        return hay.includes(q);
      });
    }

    // Tri d'apparence
    list.sort((a, b) => {
      const da = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b.createdAt ? new Date(b.createdAt).getTime() : 0;

      if (sortMode === 'oldest') return da - db;
      return db - da; // recent par défaut
    });

    return list;
  }, [accounts, filter, sortMode]);

  const totalPages = Math.max(1, Math.ceil(filteredAndSorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const visible = filteredAndSorted.slice(start, start + pageSize);

  const paginationItems = useMemo(() => {
    const items = [];
    const current = currentPage;

    items.push(
      <Pagination.First
        key="first"
        disabled={current === 1}
        onClick={() => setPage(1)}
      />,
      <Pagination.Prev
        key="prev"
        disabled={current === 1}
        onClick={() => setPage((p) => Math.max(1, p - 1))}
      />
    );

    const from = Math.max(1, current - 2);
    const to = Math.min(totalPages, current + 2);

    if (from > 1) items.push(<Pagination.Ellipsis key="ell1" disabled />);

    for (let p = from; p <= to; p += 1) {
      items.push(
        <Pagination.Item
          key={p}
          active={p === current}
          onClick={() => setPage(p)}
        >
          {p}
        </Pagination.Item>
      );
    }

    if (to < totalPages) items.push(<Pagination.Ellipsis key="ell2" disabled />);

    items.push(
      <Pagination.Next
        key="next"
        disabled={current === totalPages}
        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
      />,
      <Pagination.Last
        key="last"
        disabled={current === totalPages}
        onClick={() => setPage(totalPages)}
      />
    );

    return items;
  }, [currentPage, totalPages]);

  const openCreateAdult = () => {
    setAdultInitial(null);
    setShowAdultModal(true);
  };

  const openEditAdult = (acc) => {
    setAdultInitial({
      id: acc.id,
      clientId: acc.clientId,
      status: acc.status,
    });
    setShowAdultModal(true);
  };

  const openCreateMinor = () => {
    setMinorInitial(null);
    setShowMinorModal(true);
  };

  const openEditMinor = (acc) => {
    setMinorInitial({
      id: acc.id,
      minorId: acc.minorId,
      status: acc.status,
    });
    setShowMinorModal(true);
  };

  const handleAdultSave = (data) => {
    setErrorMsg('');
    const payload = {
      clientId: data.clientId,
      status: data.status,
    };

    const op = data.id
      ? tikaneSenpService.update(data.id, payload)
      : tikaneSenpService.create(payload);

    op
      .then(() => {
        setShowAdultModal(false);
        fetchAccounts();
      })
      .catch((e) => {
        console.error(e);
        setErrorMsg("Erreur lors de l'enregistrement du compte (adulte).");
      });
  };

  const handleMinorSave = (data) => {
    setErrorMsg('');
    const payload = {
      minorId: data.minorId,
      status: data.status,
    };

    const op = data.id
      ? tikaneSenpService.update(data.id, payload)
      : tikaneSenpService.create(payload);

    op
      .then(() => {
        setShowMinorModal(false);
        fetchAccounts();
      })
      .catch((e) => {
        console.error(e);
        setErrorMsg("Erreur lors de l'enregistrement du compte (mineur).");
      });
  };

  const handleStatusChange = (acc, newStatus) => {
    if (!canUpdate) return;
    setErrorMsg('');
    tikaneSenpService
      .update(acc.id, { status: newStatus })
      .then(fetchAccounts)
      .catch((e) => {
        console.error(e);
        setErrorMsg('Erreur lors de la mise à jour du statut.');
      });
  };

  const handleDelete = (acc) => {
    if (!canDelete) return;
    if (
      !window.confirm(
        `Voulez-vous vraiment supprimer le compte Ti Kanè Senp ${acc.accountNumber} ?`
      )
    )
      return;

    setErrorMsg('');
    tikaneSenpService
      .remove(acc.id)
      .then(fetchAccounts)
      .catch((e) => {
        console.error(e);
        setErrorMsg('Erreur lors de la suppression du compte.');
      });
  };

  const openTxModal = (acc) => {
    setActiveAccountId(acc.id);
    setTxModalShow(true);
  };

  const handleChangePageSize = (e) => {
    const value = Number(e.target.value) || 10;
    setPageSize(value);
    setPage(1);
  };

  if (!canView) {
    return (
      <Container fluid className="mt-3">
        <Alert variant="danger">
          Vous n&apos;avez pas le droit de voir les comptes Ti Kanè Senp.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Titre + barre d'outils */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} className="mb-2">
          <h3 className="fw-semibold">Gestion des comptes Ti Kanè Senp</h3>
        </Col>

        {/* Recherche */}
        <Col md={6} className="mb-2">
          <InputGroup>
            <Form.Control
              placeholder="Rechercher (type, client, mineur, compte, statut, solde…)"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            />
            {filter && (
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setFilter('');
                  setPage(1);
                }}
              >
                ×
              </Button>
            )}
          </InputGroup>
        </Col>

        {/* Trier par */}
        <Col md={3} className="mb-2 d-flex align-items-center justify-content-md-end">
          <div className="d-flex align-items-center w-100">
            <span className="me-2 text-nowrap">Trier par</span>
            <Form.Select
              size="sm"
              value={sortMode}
              onChange={(e) => {
                setSortMode(e.target.value);
                setPage(1);
              }}
            >
              <option value="recent">Plus récent → plus ancien</option>
              <option value="oldest">Plus ancien → plus récent</option>
            </Form.Select>
          </div>
        </Col>

        {/* Boutons d'action */}
        <Col
          md={3}
          className="mb-2 d-flex justify-content-md-end justify-content-start"
        >
          <ProtectedSection code="TIKANE_SENP_CREATE">
            <div className="d-flex">
              <Button
                variant="primary"
                className="fw-semibold me-2"
                onClick={openCreateAdult}
              >
                + Ouvrir un compte
              </Button>
              <Button
                variant="outline-success"
                className="fw-semibold"
                onClick={openCreateMinor}
              >
                + Compte mineur
              </Button>
            </div>
          </ProtectedSection>
        </Col>
      </Row>

      {errorMsg && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger">{errorMsg}</Alert>
          </Col>
        </Row>
      )}

      {/* Tableau */}
      <Row>
        <Col>
          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Type</th>
                <th>Client / Mineur</th>
                <th>N° Compte</th>
                <th>Solde</th>
                <th>Statut</th>
                <th style={{ minWidth: 320 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    Chargement...
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center">
                    Aucun compte Ti Kanè Senp trouvé.
                  </td>
                </tr>
              ) : (
                visible.map((acc) => {
                  const isClient = !!acc.clientId && acc.client;
                  const isMinor = !!acc.minorId && acc.minor;

                  const clientName = isClient
                    ? `${acc.client?.firstName || ''} ${
                        acc.client?.lastName || ''
                      }`.trim()
                    : '';
                  const minorName = isMinor
                    ? `${acc.minor?.firstName || ''} ${
                        acc.minor?.lastName || ''
                      }`.trim()
                    : '';

                  const ownerLabel = isClient
                    ? clientName || `Client #${acc.clientId}`
                    : isMinor
                    ? minorName || `Mineur #${acc.minorId}`
                    : 'N/A';

                  return (
                    <tr key={acc.id}>
                      <td>
                        {isClient && (
                          <Badge bg="info" text="dark">
                            Adulte
                          </Badge>
                        )}
                        {isMinor && (
                          <Badge bg="warning" text="dark">
                            Mineur
                          </Badge>
                        )}
                        {!isClient && !isMinor && <span>-</span>}
                      </td>

                      <td>{ownerLabel}</td>
                      <td>{acc.accountNumber}</td>
                      <td>{Number(acc.balance || 0).toFixed(2)}</td>
                      <td>
                        <Badge
                          bg={
                            acc.status === 'ACTIVE'
                              ? 'success'
                              : acc.status === 'CLOSED'
                              ? 'secondary'
                              : 'warning'
                          }
                        >
                          {acc.status}
                        </Badge>
                      </td>
                      <td>
                        {/* Voir */}
                        <ProtectedSection code="TIKANE_SENP_VIEW">
                          <Button
                            variant="outline-info"
                            size="sm"
                            className="me-1 mb-1"
                            onClick={() => {
                              setViewTiKaneSenpId(acc.id);
                              setViewModalShow(true);
                            }}
                          >
                            Voir
                          </Button>
                        </ProtectedSection>

                        {/* Transactions */}
                        {(canDeposit || canWithdraw || canViewTrans) && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1 mb-1"
                            onClick={() => openTxModal(acc)}
                          >
                            Transactions
                          </Button>
                        )}

                        {/* Modifier */}
                        {canUpdate && isClient && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-1 mb-1"
                            onClick={() => openEditAdult(acc)}
                          >
                            Modifier
                          </Button>
                        )}
                        {canUpdate && isMinor && (
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-1 mb-1"
                            onClick={() => openEditMinor(acc)}
                          >
                            Modifier
                          </Button>
                        )}

                        {/* Supprimer */}
                        {canDelete && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="mb-1"
                            onClick={() => handleDelete(acc)}
                          >
                            Supprimer
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Col>
      </Row>

      {/* Bas de page : résultats + pagination + taille de page */}
      <Row className="mt-2">
        <Col md={4} className="mb-2">
          <span className="text-muted">
            Résultats : {filteredAndSorted.length} • Page {currentPage} /{' '}
            {totalPages}
          </span>
        </Col>

        <Col
          md={4}
          className="mb-2 d-flex justify-content-md-center align-items-center"
        >
          <Form.Label className="me-2 mb-0">Afficher</Form.Label>
          <Form.Select
            size="sm"
            style={{ maxWidth: 110 }}
            value={pageSize}
            onChange={handleChangePageSize}
          >
            <option value={5}>5 lignes</option>
            <option value={10}>10 lignes</option>
            <option value={25}>25 lignes</option>
            <option value={50}>50 lignes</option>
            <option value={100}>100 lignes</option>
          </Form.Select>
        </Col>

        <Col
          md={4}
          className="mb-2 d-flex justify-content-md-end justify-content-start"
        >
          <Pagination size="sm" className="mb-0">
            {paginationItems}
          </Pagination>
        </Col>
      </Row>

      {/* Modal comptes adultes */}
      <TiKaneSenpModal
        show={showAdultModal}
        onHide={() => setShowAdultModal(false)}
        onSave={handleAdultSave}
        initialData={adultInitial}
      />

      {/* Modal comptes mineurs */}
      <TiKaneSenpMinorModal
        show={showMinorModal}
        onHide={() => setShowMinorModal(false)}
        onSave={handleMinorSave}
        initialData={minorInitial}
      />

      {/* Modal opérations */}
      <TransactionTiKaneSenpModal
        show={txModalShow}
        onHide={() => {
          setTxModalShow(false);
          setActiveAccountId(null);
          fetchAccounts();
        }}
        tiKaneSenpId={activeAccountId}
      />

      {/* Modal lecture seule (Voir) */}
      <TiKaneSenpViewModal
        show={viewModalShow}
        onHide={() => setViewModalShow(false)}
        tiKaneSenpId={viewTiKaneSenpId}
      />
    </Container>
  );
}
